import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import { Button } from '../../../components/ui/button';
import { Skeleton } from '../../../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';

import { 
  CheckCircle, 
  XCircle, 
  AlertCircle, 
  Eye, 
  EyeOff,
  Key,
  Database,
  Globe,
  Shield,
  Server,
  Zap,
  RefreshCw
} from 'lucide-react';

/**
 * Componente de estado del entorno
 * Muestra variables de entorno, configuraciones y estado de servicios
 */
const EnvironmentStatus = ({ status, isLoading, onRefresh }) => {
  const [showSecrets, setShowSecrets] = useState(false);

  // Obtener estado de servicios
  const getServicesStatus = () => {
    if (!status?.services) return [];
    
    return status.services.map(service => ({
      name: service.name,
      status: service.status,
      url: service.url,
      lastCheck: service.lastCheck,
      responseTime: service.responseTime,
      version: service.version
    }));
  };

  // Obtener variables de entorno
  const getEnvironmentVariables = () => {
    if (!status?.environment) return [];
    
    return Object.entries(status.environment).map(([key, value]) => ({
      key,
      value,
      isSecret: key.includes('SECRET') || key.includes('KEY') || key.includes('TOKEN'),
      isRequired: status.requiredVars?.includes(key) || false,
      isConfigured: value && value !== 'undefined' && value !== ''
    }));
  };

  // Obtener configuraciones
  const getConfigurations = () => {
    if (!status?.configurations) return [];
    
    return status.configurations.map(config => ({
      name: config.name,
      value: config.value,
      status: config.status,
      description: config.description
    }));
  };

  const services = getServicesStatus();
  const envVars = getEnvironmentVariables();
  const configurations = getConfigurations();

  // Obtener icono de estado
  const getStatusIcon = (status) => {
    switch (status) {
      case 'healthy':
      case 'connected':
      case 'active':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'warning':
      case 'degraded':
        return <AlertCircle className="h-4 w-4 text-yellow-500" />;
      case 'error':
      case 'disconnected':
      case 'inactive':
        return <XCircle className="h-4 w-4 text-red-500" />;
      default:
        return <AlertCircle className="h-4 w-4 text-gray-500" />;
    }
  };

  // Obtener badge de estado
  const getStatusBadge = (status) => {
    const variants = {
      healthy: 'default',
      connected: 'default',
      active: 'default',
      warning: 'secondary',
      degraded: 'secondary',
      error: 'destructive',
      disconnected: 'destructive',
      inactive: 'destructive'
    };

    const labels = {
      healthy: 'Healthy',
      connected: 'Connected',
      active: 'Active',
      warning: 'Warning',
      degraded: 'Degraded',
      error: 'Error',
      disconnected: 'Disconnected',
      inactive: 'Inactive'
    };

    return (
      <Badge variant={variants[status] || 'outline'}>
        {labels[status] || status}
      </Badge>
    );
  };

  // Formatear valor de variable de entorno
  const formatEnvValue = (envVar) => {
    if (!envVar.isConfigured) {
      return <span className="text-red-500 italic">Not configured</span>;
    }
    
    if (envVar.isSecret && !showSecrets) {
      return <span className="font-mono">••••••••</span>;
    }
    
    return <span className="font-mono">{envVar.value}</span>;
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <Skeleton className="h-6 w-48" />
            <Skeleton className="h-4 w-64" />
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="flex items-center justify-between p-3 border rounded-lg">
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="h-6 w-16" />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header con controles */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Environment Status</h2>
          <p className="text-muted-foreground">
            Estado de servicios, configuraciones y variables de entorno
          </p>
        </div>
        
        <Button onClick={onRefresh} variant="outline" size="sm">
          <RefreshCw className="h-4 w-4 mr-2" />
          Actualizar
        </Button>
      </div>

      {/* Tabs principales */}
      <Tabs defaultValue="services" className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="services">Servicios</TabsTrigger>
          <TabsTrigger value="environment">Variables</TabsTrigger>
          <TabsTrigger value="configurations">Configuraciones</TabsTrigger>
        </TabsList>

        {/* Servicios */}
        <TabsContent value="services">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Server className="h-5 w-5" />
                <span>Estado de Servicios</span>
              </CardTitle>
              <CardDescription>
                Conectividad y estado de servicios externos
              </CardDescription>
            </CardHeader>
            <CardContent>
              {services.length > 0 ? (
                <div className="space-y-4">
                  {services.map((service, index) => (
                    <div key={index} className="flex items-center justify-between p-4 border rounded-lg">
                      <div className="flex items-center space-x-4">
                        {getStatusIcon(service.status)}
                        <div>
                          <p className="font-medium">{service.name}</p>
                          <div className="flex items-center space-x-4 text-sm text-muted-foreground">
                            {service.url && (
                              <span>{service.url}</span>
                            )}
                            {service.responseTime && (
                              <span>{service.responseTime}ms</span>
                            )}
                            {service.version && (
                              <span>v{service.version}</span>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        {getStatusBadge(service.status)}
                        {service.url && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => window.open(service.url, '_blank')}
                          >
                            <Globe className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center text-muted-foreground py-8">
                  No hay información de servicios disponible
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Variables de entorno */}
        <TabsContent value="environment">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center space-x-2">
                    <Key className="h-5 w-5" />
                    <span>Variables de Entorno</span>
                  </CardTitle>
                  <CardDescription>
                    Configuración de variables de entorno y secretos
                  </CardDescription>
                </div>
                
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowSecrets(!showSecrets)}
                >
                  {showSecrets ? (
                    <>
                      <EyeOff className="h-4 w-4 mr-2" />
                      Ocultar
                    </>
                  ) : (
                    <>
                      <Eye className="h-4 w-4 mr-2" />
                      Mostrar
                    </>
                  )}
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {envVars.length > 0 ? (
                <div className="space-y-3">
                  {envVars.map((envVar, index) => (
                    <div key={index} className="flex items-center justify-between p-3 border rounded-lg">
                      <div className="flex items-center space-x-3">
                        {envVar.isSecret ? (
                          <Shield className="h-4 w-4 text-yellow-500" />
                        ) : (
                          <Key className="h-4 w-4 text-muted-foreground" />
                        )}
                        
                        <div>
                          <div className="flex items-center space-x-2">
                            <span className="font-medium">{envVar.key}</span>
                            {envVar.isRequired && (
                              <Badge variant="outline" className="text-xs">
                                Required
                              </Badge>
                            )}
                            {envVar.isSecret && (
                              <Badge variant="secondary" className="text-xs">
                                Secret
                              </Badge>
                            )}
                          </div>
                          <div className="text-sm text-muted-foreground">
                            {formatEnvValue(envVar)}
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        {envVar.isConfigured ? (
                          <CheckCircle className="h-4 w-4 text-green-500" />
                        ) : (
                          <XCircle className="h-4 w-4 text-red-500" />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center text-muted-foreground py-8">
                  No hay variables de entorno disponibles
                </div>
              )}

              {/* Resumen */}
              {envVars.length > 0 && (
                <div className="mt-6 pt-6 border-t">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
                    <div>
                      <p className="text-2xl font-bold text-[var(--yool-success)]">
                        {envVars.filter(v => v.isConfigured).length}
                      </p>
                      <p className="text-sm text-muted-foreground">Configuradas</p>
                    </div>
                    
                    <div>
                      <p className="text-2xl font-bold text-[var(--yool-error)]">
                        {envVars.filter(v => !v.isConfigured).length}
                      </p>
                      <p className="text-sm text-muted-foreground">Faltantes</p>
                    </div>
                    
                    <div>
                      <p className="text-2xl font-bold text-yellow-600">
                        {envVars.filter(v => v.isSecret).length}
                      </p>
                      <p className="text-sm text-muted-foreground">Secretos</p>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Configuraciones */}
        <TabsContent value="configurations">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Zap className="h-5 w-5" />
                <span>Configuraciones</span>
              </CardTitle>
              <CardDescription>
                Configuraciones de la aplicación y servicios
              </CardDescription>
            </CardHeader>
            <CardContent>
              {configurations.length > 0 ? (
                <div className="space-y-4">
                  {configurations.map((config, index) => (
                    <div key={index} className="flex items-center justify-between p-4 border rounded-lg">
                      <div>
                        <p className="font-medium">{config.name}</p>
                        <p className="text-sm text-muted-foreground">
                          {config.description}
                        </p>
                        <p className="text-sm font-mono mt-1">
                          {config.value}
                        </p>
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        {getStatusIcon(config.status)}
                        {getStatusBadge(config.status)}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center text-muted-foreground py-8">
                  No hay configuraciones disponibles
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default EnvironmentStatus;